import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ShieldAlert, MessageCircle, ThumbsUp, AlertTriangle, HelpCircle, Plus, Check, Loader2, ArrowUp, ArrowDown, RotateCcw, BellRing } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CustomerPulsePanel } from "@/components/feedback/CustomerPulsePanel";

const TYPE_META = {
  compliment: { icon: ThumbsUp,      colour: "text-emerald-700",  bg: "bg-emerald-50 border-emerald-200", label: "Compliment" },
  complaint:  { icon: AlertTriangle, colour: "text-rose-700",     bg: "bg-rose-50 border-rose-200",       label: "Complaint" },
  query:      { icon: HelpCircle,    colour: "text-sky-700",      bg: "bg-sky-50 border-sky-200",         label: "Query" },
};

const STATUS_COLOURS = {
  open:        "bg-amber-100 text-amber-800",
  in_progress: "bg-sky-100 text-sky-800",
  resolved:    "bg-emerald-100 text-emerald-800",
  closed:      "bg-slate-100 text-slate-600",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

const GRID = { gridTemplateColumns: "0.9fr 1.7fr 1fr 0.65fr 0.85fr 0.85fr 0.6fr" };

export default function FeedbackPage() {
  const { can, loaded } = usePermissions();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("all");
  const [bus, setBus] = useState([]);
  const [clients, setClients] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ type: "compliment", subject: "", description: "", source: "portal", client_id: "", business_unit_id: "", reported_at: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ type: "all", subject: "", client: "", bu: "all", status: "all" });
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [escOpen, setEscOpen] = useState(false);
  const [esc, setEsc] = useState({ enabled: true, l1_days: 7, l2_days: 30 });
  const [escBusy, setEscBusy] = useState(false);

  const openEscSettings = async () => {
    try {
      const r = await api.get("/feedback/escalation-settings");
      setEsc(r.data);
    } catch (e) { /* defaults */ }
    setEscOpen(true);
  };

  const saveEsc = async () => {
    setEscBusy(true);
    try {
      const r = await api.put("/feedback/escalation-settings", esc);
      setEsc(r.data);
      toast.success("Escalation settings saved");
      setEscOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEscBusy(false); }
  };

  const runEscNow = async () => {
    setEscBusy(true);
    try {
      const r = await api.post("/feedback/escalations/run");
      toast.success(`Escalation check done — ${r.data.l1_sent || 0} BU-manager, ${r.data.l2_sent || 0} executive email(s) sent`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEscBusy(false); }
  };

  const load = async () => {
    if (tab === "pulse") return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "500");
      if (tab !== "all") {
        if (["open", "in_progress", "resolved", "closed"].includes(tab)) params.set("status", tab);
        else params.set("type", tab);
      }
      const [{ data: rows }, { data: sum }] = await Promise.all([
        api.get(`/feedback${params.toString() ? `?${params}` : ""}`),
        api.get(`/feedback/summary`),
      ]);
      setItems(rows);
      setSummary(sum);
    } catch (e) {
      toast.error("Failed to load feedback");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!loaded || !can("crm", "view")) return;
    api.get("/org/business-units").then((r) => setBus((r.data || []).filter((b) => b.active !== false))).catch(() => {});
    api.get("/master/clients").then((r) => setClients(r.data || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, [loaded]);

  useEffect(() => {
    if (!loaded || !can("crm", "view")) return;
    load();
    /* eslint-disable-next-line */
  }, [tab, loaded]);

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Subject is required"); return; }
    if (!form.reported_at) { toast.error("Date reported is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_id) delete payload.client_id;
      if (!payload.business_unit_id) delete payload.business_unit_id;
      if (!payload.description) delete payload.description;
      await api.post("/feedback", payload);
      toast.success("Feedback logged");
      setCreateOpen(false);
      setForm({ type: "compliment", subject: "", description: "", source: "portal", client_id: "", business_unit_id: "", reported_at: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resolve = async (id) => setStatus(id, "resolved");

  const setStatus = async (id, status) => {
    try {
      await api.put(`/feedback/${id}`, { status });
      toast.success(`Status changed to ${status.replace("_", " ")}`);
      setDetail((d) => (d && d.id === id ? { ...d, status } : d));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setBu = async (id, business_unit_id) => {
    try {
      await api.put(`/feedback/${id}`, { business_unit_id });
      const bu = bus.find((b) => b.id === business_unit_id);
      toast.success(`Business unit set to ${bu ? bu.code : business_unit_id}`);
      setDetail((d) => (d && d.id === id ? { ...d, business_unit_id } : d));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!loaded) return <AppLayout title="Feedback"><div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" /></AppLayout>;
  if (!can("crm", "view")) {
    return (
      <AppLayout title="Feedback">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">Access restricted</h2>
        </div>
      </AppLayout>
    );
  }

  const canCreate = can("crm", "create");
  const canEdit = can("crm", "edit");
  const bu_name = (id) => bus.find((b) => b.id === id)?.code || "—";
  const client_name = (id) => clients.find((c) => c.id === id)?.name || "—";

  const sortVal = (f, key) => {
    if (key === "client") return client_name(f.client_id).toLowerCase();
    if (key === "bu") return bu_name(f.business_unit_id).toLowerCase();
    return String(f[key] || "").toLowerCase();
  };
  const shown = items
    .filter((f) => {
      if (filters.type !== "all" && f.type !== filters.type) return false;
      if (filters.status !== "all" && f.status !== filters.status) return false;
      if (filters.bu !== "all" && f.business_unit_id !== filters.bu) return false;
      if (filters.subject && !`${f.subject || ""} ${f.description || ""}`.toLowerCase().includes(filters.subject.toLowerCase())) return false;
      if (filters.client && !client_name(f.client_id).toLowerCase().includes(filters.client.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
      return (va < vb ? -1 : va > vb ? 1 : 0) * (sort.dir === "asc" ? 1 : -1);
    });
  const Th = ({ k, label }) => (
    <button type="button" data-testid={`fb-col-${k}`}
            onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}
            className="flex items-center gap-1 uppercase tracking-wider text-left hover:opacity-80">
      {label}
      {sort.key === k && (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
    </button>
  );

  return (
    <AppLayout
      title="Feedback Register"
      subtitle="Log compliments, complaints and queries — feeds QMS Customer Service KPIs automatically"
      actions={
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" data-testid="fb-escalation-settings-btn" onClick={openEscSettings}>
              <BellRing className="w-4 h-4 mr-1.5" /> Escalations
            </Button>
          )}
          {canCreate && (
            <Button data-testid={TID.fbCreateOpen} onClick={() => setCreateOpen(true)} className="bg-gbe hover:bg-gbe/90 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Log feedback
            </Button>
          )}
        </div>
      }
    >
      {/* KPI tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> Total entries</div>
            <div className="mt-1 font-display text-2xl text-slate-900">{summary.total}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><ThumbsUp className="w-3.5 h-3.5 text-emerald-600" /> Compliments</div>
            <div className="mt-1 font-display text-2xl text-emerald-700">{summary.by_type.find((t) => t.type === "compliment")?.count || 0}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> Complaints</div>
            <div className="mt-1 font-display text-2xl text-rose-700">{summary.by_type.find((t) => t.type === "complaint")?.count || 0}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Outstanding</div>
            <div className="mt-1 font-display text-2xl text-amber-700">{summary.outstanding_complaints}</div>
            <div className="text-xs text-slate-500">complaints still open</div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
          <TabsTrigger value="all" className="text-xs data-[state=active]:bg-white">All</TabsTrigger>
          <TabsTrigger value="compliment" className="text-xs data-[state=active]:bg-white">Compliments</TabsTrigger>
          <TabsTrigger value="complaint" className="text-xs data-[state=active]:bg-white">Complaints</TabsTrigger>
          <TabsTrigger value="query" className="text-xs data-[state=active]:bg-white">Queries</TabsTrigger>
          <TabsTrigger value="open" className="text-xs data-[state=active]:bg-white">Open only</TabsTrigger>
          <TabsTrigger value="pulse" data-testid="fb-tab-pulse" className="text-xs data-[state=active]:bg-white">Customer Pulse (CSAT)</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {tab === "pulse" ? (
            <CustomerPulsePanel canSend={can("performance", "edit")} />
          ) : (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid gap-2 px-4 py-2.5 text-[11px] text-white bg-gbe items-center" style={GRID}>
              <Th k="type" label="Type" />
              <Th k="subject" label="Subject" />
              <Th k="client" label="Client" />
              <Th k="bu" label="BU" />
              <Th k="created_at" label="Reported" />
              <Th k="status" label="Status" />
              <div></div>
            </div>
            <div className="grid gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 items-center" style={GRID}>
              <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
                <SelectTrigger data-testid="fb-filter-type" className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="compliment">Compliment</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                  <SelectItem value="query">Query</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="fb-filter-subject" value={filters.subject} placeholder="Search subject / comment…"
                     onChange={(e) => setFilters({ ...filters, subject: e.target.value })} className="h-7 text-xs" />
              <Input data-testid="fb-filter-client" value={filters.client} placeholder="Client…"
                     onChange={(e) => setFilters({ ...filters, client: e.target.value })} className="h-7 text-xs" />
              <Select value={filters.bu} onValueChange={(v) => setFilters({ ...filters, bu: v })}>
                <SelectTrigger data-testid="fb-filter-bu" className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {bus.map((b) => <SelectItem key={b.id} value={b.id}>{b.code}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-400">{shown.length} of {items.length}</div>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="fb-filter-status" className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-right">
                <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" data-testid="fb-filter-clear"
                        onClick={() => setFilters({ type: "all", subject: "", client: "", bu: "all", status: "all" })}>
                  Clear
                </Button>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {busy && <div className="p-10 text-center text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>}
              {!busy && shown.length === 0 && (
                <div className="p-10 text-center text-sm text-slate-500">No entries.</div>
              )}
              {shown.map((f) => {
                const meta = TYPE_META[f.type];
                const Icon = meta?.icon || MessageCircle;
                return (
                  <div key={f.id} data-testid={TID.fbRow(f.id)} onClick={() => setDetail(f)}
                       className="grid gap-2 px-4 py-3 text-sm items-center cursor-pointer hover:bg-slate-50" style={GRID}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${meta?.colour || "text-slate-500"}`} />
                      <span className="text-slate-700">{meta?.label || f.type}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-slate-900 font-medium truncate">{f.subject}</div>
                      {f.description && <div className="text-xs text-slate-500 truncate">{f.description}</div>}
                    </div>
                    <div className="truncate text-slate-600">{client_name(f.client_id)}</div>
                    <div className="truncate text-slate-600 font-mono text-xs">{bu_name(f.business_unit_id)}</div>
                    <div className="text-xs text-slate-600 tabular-nums" data-testid={`fb-reported-${f.id}`}>{fmtDate(f.created_at)}</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={STATUS_COLOURS[f.status] || "bg-slate-100 text-slate-700"}>{f.status?.replace("_", " ")}</Badge>
                      {f.status !== "resolved" && f.status !== "closed" && f.escalated_l2_at && (
                        <Badge data-testid={`fb-esc-badge-${f.id}`} className="bg-rose-600 text-white">ESC L2</Badge>
                      )}
                      {f.status !== "resolved" && f.status !== "closed" && !f.escalated_l2_at && f.escalated_l1_at && (
                        <Badge data-testid={`fb-esc-badge-${f.id}`} className="bg-amber-500 text-white">ESC L1</Badge>
                      )}
                    </div>
                    <div className="text-right" onClick={(e) => e.stopPropagation()}>
                      {f.status !== "resolved" && f.status !== "closed" && canEdit && (
                        <Button data-testid={TID.fbResolve(f.id)} onClick={() => resolve(f.id)} variant="ghost" size="sm" className="h-8 text-emerald-700 hover:bg-emerald-50">
                          <Check className="w-3.5 h-3.5 mr-1" /> Resolve
                        </Button>
                      )}
                      {(f.status === "resolved" || f.status === "closed") && canEdit && (
                        <Button data-testid={`fb-reopen-${f.id}`} onClick={() => setStatus(f.id, "open")} variant="ghost" size="sm" className="h-8 text-amber-700 hover:bg-amber-50" title="Reopen — back to unresolved">
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl" data-testid="fb-detail-dialog">
          {detail && (() => {
            const meta = TYPE_META[detail.type];
            const Icon = meta?.icon || MessageCircle;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-start gap-2 text-base pr-6">
                    <Icon className={`w-4 h-4 mt-1 shrink-0 ${meta?.colour || "text-slate-500"}`} />
                    <span>{detail.subject}</span>
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    {meta?.label || detail.type}
                    {detail.severity ? ` · ${detail.severity} severity` : ""} · reported {fmtDate(detail.created_at)}
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-64 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 whitespace-pre-wrap"
                     data-testid="fb-detail-comment">
                  {detail.description || <span className="text-slate-400">No further detail was recorded.</span>}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <div><span className="text-slate-400">Client</span><div className="text-slate-700">{client_name(detail.client_id)}</div></div>
                  <div><span className="text-slate-400">Business unit</span>
                    {canEdit ? (
                      <Select value={detail.business_unit_id || ""} onValueChange={(v) => setBu(detail.id, v)}>
                        <SelectTrigger data-testid="fb-detail-bu-select" className={`mt-0.5 h-8 text-xs ${detail.business_unit_id ? "" : "border-amber-400 text-amber-700"}`}>
                          <SelectValue placeholder="Assign BU…" />
                        </SelectTrigger>
                        <SelectContent>
                          {bus.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-slate-700 font-mono">{bu_name(detail.business_unit_id)}</div>
                    )}
                  </div>
                  <div><span className="text-slate-400">Source</span><div className="text-slate-700">{(detail.source || "—").replace("_", " ")}</div></div>
                  <div><span className="text-slate-400">Submitted by</span><div className="text-slate-700">{detail.submitted_by_name || detail.submitted_by_email || detail.submitted_by || "—"}</div></div>
                  <div><span className="text-slate-400">Date reported</span><div className="text-slate-700">{fmtDate(detail.created_at)}</div></div>
                  <div><span className="text-slate-400">Resolved</span><div className="text-slate-700">{detail.resolved_at ? `${fmtDate(detail.resolved_at)}${detail.resolved_by ? ` by ${detail.resolved_by}` : ""}` : "—"}</div></div>
                  {(detail.escalated_l1_at || detail.escalated_l2_at) && (
                    <div className="col-span-2"><span className="text-slate-400">Escalations</span>
                      <div className="text-slate-700">
                        {detail.escalated_l1_at && <>L1 → BU manager on {fmtDate(detail.escalated_l1_at)}</>}
                        {detail.escalated_l1_at && detail.escalated_l2_at && " · "}
                        {detail.escalated_l2_at && <>L2 → executives &amp; ops on {fmtDate(detail.escalated_l2_at)}</>}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter className="items-center gap-2 sm:justify-between">
                  <Badge className={STATUS_COLOURS[detail.status] || "bg-slate-100 text-slate-700"}>{detail.status?.replace("_", " ")}</Badge>
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Change status</span>
                      <Select value={detail.status} onValueChange={(v) => setStatus(detail.id, v)}>
                        <SelectTrigger data-testid="fb-detail-status-select" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open (unresolved)</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={escOpen} onOpenChange={setEscOpen}>
        <DialogContent className="max-w-md" data-testid="fb-escalation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <BellRing className="w-4 h-4 text-amber-600" /> Complaint escalation settings
            </DialogTitle>
            <DialogDescription className="text-xs">
              A daily check emails the BU manager when a complaint is still unresolved after Level 1,
              then the executive team + operations management after Level 2. One email per level.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-700">Escalation emails enabled</div>
              <Switch data-testid="fb-esc-enabled-switch" checked={esc.enabled}
                      onCheckedChange={(v) => setEsc({ ...esc, enabled: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Level 1 — BU manager after</label>
                <Select value={String(esc.l1_days)} onValueChange={(v) => setEsc({ ...esc, l1_days: Number(v) })}>
                  <SelectTrigger data-testid="fb-esc-l1-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[3, 5, 7, 10, 14, 21].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Level 2 — executives &amp; ops after</label>
                <Select value={String(esc.l2_days)} onValueChange={(v) => setEsc({ ...esc, l2_days: Number(v) })}>
                  <SelectTrigger data-testid="fb-esc-l2-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[14, 21, 30, 45, 60, 90].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-[11px] text-slate-400">
              Level 1 falls back to the parent-BU manager, then executives, if the BU has no manager assigned.
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" size="sm" onClick={runEscNow} disabled={escBusy}
                    data-testid="fb-esc-run-now-btn" className="text-xs">
              {escBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Run check now
            </Button>
            <Button onClick={saveEsc} disabled={escBusy} data-testid="fb-esc-save-btn"
                    className="bg-gbe hover:bg-gbe/90 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log a piece of feedback</DialogTitle>
            <DialogDescription>Takes 15 seconds. Feeds the QMS Customer Service KPIs automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Type *</label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid={TID.fbCreateType} className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="compliment">Compliment</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                  <SelectItem value="query">Query</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Subject *</label>
              <Input data-testid={TID.fbCreateSubject} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Great job on the emergency callout" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional detail" className="mt-1" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Client</label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">BU affected</label>
                <Select value={form.business_unit_id} onValueChange={(v) => setForm({ ...form, business_unit_id: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {bus.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Source</label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portal">Hub portal</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="in_person">In person</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Date reported *</label>
                <Input type="date" data-testid="fb-create-reported-at" value={form.reported_at}
                       max={new Date().toISOString().slice(0, 10)}
                       onChange={(e) => setForm({ ...form, reported_at: e.target.value })} className="mt-1" />
                <div className="text-[11px] text-slate-400 mt-1">When the feedback actually happened — used for the register and monthly KPIs.</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid={TID.fbCreateSubmit} onClick={submit} disabled={saving} className="bg-gbe hover:bg-gbe/90 text-white">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : "Log feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
